// client/src/services/insightsAPI.js
// API service for AI insights — every endpoint accepts an optional
// { from, to } date range that is forwarded as query parameters.

import api from './api';
import { extractSoftPaywall, dispatchSoftPaywall } from '../utils/softPaywall';

// ── helpers ──────────────────────────────────────────────────────────────────
const cleanParams = (obj = {}) => {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  });
  return out;
};


/** Ensure 402 soft-paywall surfaces even if a caller swallows the axios error. */
const withSoftPaywall = async (fn) => {
  try {
    return await fn();
  } catch (error) {
    const soft = extractSoftPaywall(error);
    if (soft) {
      dispatchSoftPaywall(soft);
      error.softPaywall = soft;
    }
    throw error;
  }
};

// ── core insights ────────────────────────────────────────────────────────────
export const getAllInsights = async ({ from, to } = {}) => withSoftPaywall(async () => {
  const response = await api.get('/insights', {
    params: cleanParams({ from, to }),
    timeout: 60000,
  });
  return response.data;
});

export const getSpendingInsights = async ({ period = 'month', from, to } = {}) => {
  const response = await api.get('/insights/spending', {
    params: cleanParams({ period, from, to }),
  });
  return response.data;
};

export const getAnomalies = async (daysOrOpts = 90, maybeOpts) => {
  const opts = (typeof daysOrOpts === 'object' && daysOrOpts !== null)
    ? daysOrOpts
    : { days: daysOrOpts, ...(maybeOpts || {}) };
  const { days = 90, from, to } = opts;
  const response = await api.get('/insights/anomalies', {
    params: cleanParams({ days, from, to }),
  });
  return response.data;
};

export const getRecurringTransactions = async (minOrOpts = 2, maybeOpts) => {
  const opts = (typeof minOrOpts === 'object' && minOrOpts !== null)
    ? minOrOpts
    : { minOccurrences: minOrOpts, ...(maybeOpts || {}) };
  const { minOccurrences = 2, from, to } = opts;
  const response = await api.get('/insights/recurring', {
    params: cleanParams({ minOccurrences, from, to }),
  });
  return response.data;
};

export const getBalancePrediction = async (daysOrOpts = 7, maybeOpts) => {
  const opts = (typeof daysOrOpts === 'object' && daysOrOpts !== null)
    ? daysOrOpts
    : { days: daysOrOpts, ...(maybeOpts || {}) };
  const { days = 7, from, to } = opts;
  const response = await api.get('/insights/prediction', {
    params: cleanParams({ days, from, to }),
    timeout: 45000,
  });
  return response.data;
};

export const getInsightStats = async ({ from, to } = {}) => {
  const response = await api.get('/insights/stats', { params: cleanParams({ from, to }) });
  return response.data;
};

export const chatWithAI = async (question, { from, to } = {}) => withSoftPaywall(async () => {
  const response = await api.post('/insights/chat',
    cleanParams({ question, from, to }),
    { timeout: 60000 }
  );
  return response.data;
});

export const getSavingsOpportunities = async ({ from, to } = {}) => {
  const response = await api.get('/insights/savings-opportunities', {
    params: cleanParams({ from, to }),
    timeout: 45000,
  });
  return response.data;
};

export const getWhatIfAnalysis = (data, { from, to } = {}) =>
  api.post('/insights/whatif', data, {
    params: cleanParams({ from, to }),
    timeout: 45000,
  }).then(r => r.data);

export const getGoals = ({ from, to } = {}) =>
  api.get('/insights/goals', { params: cleanParams({ from, to }) }).then(r => r.data);

export const createGoal = (data) =>
  api.post('/insights/goals', data).then(r => r.data);

export const deleteGoal = (id) =>
  api.delete(`/insights/goals/${id}`).then(r => r.data);

export const getCashFlowCalendar = ({ from, to } = {}) =>
  api.get('/insights/cashflow-calendar', { params: cleanParams({ from, to }) }).then(r => r.data);

export const getImpulseSpending = ({ from, to } = {}) =>
  api.get('/insights/impulse-spending', { params: cleanParams({ from, to }) }).then(r => r.data);

export const getBudgetScorecard = ({ from, to } = {}) =>
  api.get('/insights/budget-scorecard', { params: cleanParams({ from, to }) }).then(r => r.data);

export const getMerchantIntelligence = ({ from, to } = {}) =>
  api.get('/insights/merchant-intelligence', { params: cleanParams({ from, to }) }).then(r => r.data);

// Canonical merchant data source — backed by /api/analytics/merchants.
// Use this in new code; getMerchantIntelligence is now a deprecated proxy.
export const getAnalyticsMerchants = ({ from, to, limit = 50 } = {}) =>
  api.get('/analytics/merchants', { params: cleanParams({ from, to, limit }) }).then(r => r.data);

export const getFireNumber = ({ from, to } = {}) =>
  api.get('/insights/fire-number', { params: cleanParams({ from, to }) }).then(r => r.data);

export const getSeasonalForecast = ({ from, to } = {}) =>
  api.get('/insights/seasonal-forecast', { params: cleanParams({ from, to }) }).then(r => r.data);

export const getHealthScore = ({ from, to, ...rest } = {}) =>
  api.get('/insights/health-score', {
    params: cleanParams({ from, to, ...rest }),
    timeout: 45000,
  }).then(r => r.data);

// Drill-down: paginated transactions for a merchant within an optional range.
// Backed by the existing analytics endpoint.
export const getMerchantTransactions = (merchantKey, { from, to, page = 1, limit = 50 } = {}) =>
  api.get(`/analytics/merchants/${encodeURIComponent(merchantKey)}/transactions`, {
    params: cleanParams({ from, to, page, limit }),
  }).then(r => r.data);

const insightsAPI = {
  getAllInsights,
  getSpendingInsights,
  getAnomalies,
  getRecurringTransactions,
  getBalancePrediction,
  getInsightStats,
  chatWithAI,
  getSavingsOpportunities,
  getWhatIfAnalysis,
  getGoals,
  createGoal,
  deleteGoal,
  getCashFlowCalendar,
  getImpulseSpending,
  getBudgetScorecard,
  getMerchantIntelligence,
  getAnalyticsMerchants,
  getMerchantTransactions,
  getFireNumber,
  getSeasonalForecast,
  getHealthScore,
};
export default insightsAPI;
